/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { transaction } from 'mobx';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { labelingInteractions, LabelingInteractionsAction } from 'src/luis/projects/utterances/handlers/labelingInteractions';
import {
    fromInteractionsLabelToLabelStore,
    fromLabelStoreToInteractionsLabel
} from 'src/luis/projects/utterances/handlers/labelingInteractionsUtils';
import { ModelIdentifier } from 'src/luis/projects/utterances/handlers/utteranceHandlerGroup';
import { AddExampleSwaggerPayload, BatchAddExampleSwagger } from 'src/luis/projects/utterances/services/mappers';
import { getUtteranceService } from 'src/luis/projects/utterances/services/utteranceService';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { getLabelingStores, toLabelsSwagger } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

export type LabelingHandlerGroup = ReturnType<typeof createLabelingHandlerGroup>;

type SubmitLabelsParams = HandlerParams<
    ModelIdentifier & {
        utteranceId: LuisId;
        intentId: string;
        userAction: LabelingInteractionsAction;
    }
>;

/**
 * Creates a handler group of functions for the labeling interactions to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createLabelingHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();

    const getCurrentAppVersionStore = ({ appId, versionId }: VersionedAppId) =>
        rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);

    const enableSendingLabelsInNestedFormat = true;

    /**
     * Uses labelingInteractions function to send labels to the backend optimistically,
     * revert changes and logs the error if a failure occurs.
     *
     * @param param0.intentId The id of the intent having the label.
     * @param param0.utteranceId The id of the utterance having the label.
     * @param param0.model The id and typeIf of the model (entity / intent) having the label.
     * @param param0.userAction The action type and the label to apply the action on.
     */
    const submitLabels = async ({ intentId, utteranceId, model, userAction, context }: SubmitLabelsParams) =>
        flow(async () => {
            const { appId, versionId } = context;
            const { entityStoreHouse, intentStoreHouse, utteranceStoreHouse } = getLabelingStores({
                model,
                currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
            });
            const intentName = intentStoreHouse.findById(intentId).name;
            const utterance = utteranceStoreHouse.findById(utteranceId);

            const fallBackLabels = [...utterance.labeledEntities];

            // Get the new labels
            const { labels: newLabelStores } = labelingInteractions(
                { labels: utterance.labeledEntities.map(fromLabelStoreToInteractionsLabel), entities: entityStoreHouse.items },
                userAction
            );

            const newEntityStoreLabels = newLabelStores.map(fromInteractionsLabelToLabelStore);
            // Updates the utterance labels optimistically
            utterance.setLabels(newEntityStoreLabels);

            // Get the label swaggers
            const entityLabels = toLabelsSwagger(
                {
                    entityStores: entityStoreHouse.items,
                    text: utterance.text,
                    tokenizedText: utterance.tokenizedText,
                    labels: newEntityStoreLabels
                },
                enableSendingLabelsInNestedFormat
            );

            const logTimedActivity = logger.createTimedActivityLogger<UtteranceStore>(userAction.name, () => ({
                intentId,
                utteranceId: utterance.id,
                labelsCount: entityLabels.length
            }));

            try {
                const utteranceService = getUtteranceService({ context });
                await logTimedActivity(
                    utteranceService.addUtterance({
                        payload: { text: utterance.text, intentName, entityLabels },
                        intentId,
                        enableSendingLabelsInNestedFormat
                    })
                );
            } catch (e) {
                // Revert the changes in case of failure
                utterance.setLabels(fallBackLabels);

                // @TODO: We need to log this error somehow.
                throw new Error((<Error>e).message);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Accept the predicted labels for each utterance and add them to the utterance store as labels.
     * If the operation failed the following steps are followed:
     * 1- Given the fallback labels which we need to add
     * 2- Get the utterances given from the given ids
     * 3- Get the labeled entities for each utterance
     * 4- removes the label it is one of the fallback labels
     *
     * @param param0.intentId The id of the intent to add the label to.
     * @param param0.ids The id of the current utterance.
     * @param param0.model Model id and typeId of the app.
     */
    const confirmUtterancesPredictions = async ({
        intentId,
        utteranceIds,
        model,
        context
    }: HandlerParams<ModelIdentifier & { intentId: string; utteranceIds: LuisId[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const { predictionStoreHouse, entityStoreHouse, intentStoreHouse, utteranceStoreHouse } = getLabelingStores({
                model,
                currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
            });
            let intentName = intentStoreHouse.findById(intentId)?.name;
            const fallbackLabelsMap: Map<LuisId, EntityLabelStore[]> = utteranceIds.reduce(
                (utterancesMap, id) => utterancesMap.set(id, [...utteranceStoreHouse.findById(id).labeledEntities]),
                new Map()
            );

            const utterances = utteranceIds.map(id => utteranceStoreHouse.findById(id));
            const payload: AddExampleSwaggerPayload[] = [];

            transaction(() => {
                for (const utterance of utterances) {
                    const { predictedEntities } = predictionStoreHouse.predictions.get(utterance.text);
                    const mlPredictedEntities = predictedEntities.filter(({ entityId }) => {
                        const entityStore = entityStoreHouse.findById(entityId);

                        if (entityStore) {
                            return isMachineLearned(entityStore.typeId);
                        }

                        return true;
                    });

                    // Get the new labels
                    const { labels: newLabelStores } = labelingInteractions(
                        {
                            labels: utterance.labeledEntities.map(fromLabelStoreToInteractionsLabel),
                            entities: entityStoreHouse.items
                        },
                        {
                            name: 'ConfirmPredictions',
                            labels: mlPredictedEntities.map(prediction => ({
                                start: prediction.startTokenIndex,
                                end: prediction.endTokenIndex,
                                entityId: prediction.entityId.toString(),
                                roleId: prediction.roleId?.toString()
                            }))
                        }
                    );

                    utterance.setLabels(newLabelStores.map(fromInteractionsLabelToLabelStore));

                    const entityLabels = toLabelsSwagger(
                        {
                            entityStores: entityStoreHouse.items,
                            text: utterance.text,
                            tokenizedText: utterance.tokenizedText,
                            labels: utterance.labeledEntities
                        },
                        enableSendingLabelsInNestedFormat
                    );

                    if (!intentName) {
                        intentName = intentStoreHouse.findById(utterance.labeledIntentId).name;
                    }

                    payload.push({
                        text: utterance.text,
                        intentName,
                        entityLabels
                    });
                }
            });

            const logTimedActivity = logger.createTimedActivityLogger<BatchAddExampleSwagger>('ConfirmUtterancesPredictions', () => ({
                modelId: model.id,
                intentId,
                ids: utteranceIds
            }));

            const utterancesSwagger = await logTimedActivity(
                getUtteranceService({ context }).addUtterances({
                    payload,
                    enableSendingLabelsInNestedFormat
                })
            );

            const failedUtterancesIds = utterancesSwagger.filter(u => u.hasError).map(u => u.value.ExampleId);

            if (failedUtterancesIds.length) {
                transaction(() => {
                    const utterancesToRevert = failedUtterancesIds.map(id => utteranceStoreHouse.findById(id));

                    utterancesToRevert.forEach(utterance => {
                        const labelsToRevert = fallbackLabelsMap.get(utterance.id);

                        utterance.setLabels(labelsToRevert);
                    });

                    // @TODO: We need to log this error somehow.
                    throw new Error(`${failedUtterancesIds.length} utterances failed`);
                });
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return { submitLabels, confirmUtterancesPredictions };
};
